import React, { useEffect, useState } from 'react';
import { usePWAInstall } from '../hooks/usePWAInstall';

// VAV School Logo SVG inline
const VAVLogoIcon: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#9333ea" />
    <text x="24" y="31" textAnchor="middle" fontFamily="Inter,sans-serif" fontWeight="900"
      fontSize="18" fill="white">vOS</text>
  </svg>
);

const ShareIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="#9333ea" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

export const PWAInstallBanner: React.FC = () => {
  const { installState, triggerInstall, dismiss } = usePWAInstall();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (installState === 'android-ready' || installState === 'ios-manual') {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [installState]);

  if (!visible) return null;

  const base: React.CSSProperties = {
    position: 'fixed',
    bottom: 80,
    left: 16,
    right: 16,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '12px 14px',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.92)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '0.5px solid #e2e8f0',
    boxShadow: '0px 4px 20px 0px rgba(147,51,234,0.15), 0px 2px 8px rgba(0,0,0,0.08)',
    animation: 'pwaSlideUp 0.3s ease-out both',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  };

  if (installState === 'android-ready') {
    return (
      <div style={base}>
        <VAVLogoIcon size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.title}>Install VAV Teacher</div>
          <div style={styles.subtitle}>Add to home screen for quick access</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button style={styles.skipBtn} onClick={dismiss}>Skip</button>
          <button
            style={styles.installBtn}
            onClick={async () => {
              const ok = await triggerInstall();
              if (!ok) dismiss();
            }}
          >
            Install
          </button>
        </div>
      </div>
    );
  }

  if (installState === 'ios-manual') {
    return (
      <div style={{ ...base, flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <VAVLogoIcon size={40} />
          <div style={{ flex: 1 }}>
            <div style={styles.title}>Install VAV Teacher</div>
            <div style={styles.subtitle}>Add to home screen</div>
          </div>
          <button style={styles.skipBtn} onClick={dismiss}>✕</button>
        </div>
        <div style={styles.iosStrip}>
          <span>Tap</span>
          <ShareIcon />
          <span>then</span>
          <span style={{ color: '#9333ea', fontWeight: 600 }}>"Add to Home Screen"</span>
        </div>
      </div>
    );
  }

  return null;
};

const styles: Record<string, React.CSSProperties> = {
  title: {
    fontSize: 13,
    fontWeight: 700,
    color: '#06050D',
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontSize: 11,
    color: '#64748b',
    lineHeight: 1.3,
    marginTop: 2,
  },
  skipBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: 12,
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '4px 6px',
    fontFamily: 'inherit',
  },
  installBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: 12,
    fontWeight: 700,
    color: '#9333ea',
    cursor: 'pointer',
    padding: '4px 6px',
    fontFamily: 'inherit',
  },
  iosStrip: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#64748b',
    background: '#f3e8ff',
    borderRadius: 8,
    padding: '8px 12px',
  },
};

export default PWAInstallBanner;
